// Placeholder data for the State Livestock Overview page (Overview map →
// "View more"). Structure/shape only — swap for real public market,
// facility and professional endpoints once they exist.
//
// Rows are generated deterministically per state (same state → same rows on
// every visit) and the row counts match the Overview panel's figures from
// getStateDetails, so "Livestock Markets: 250" opens a table of 250 markets.

import { NIGERIA_STATES_GRID, getStateDetails } from './overviewData';

// Display names as the Overview map uses them (FCT is spelled out there)
export const OVERVIEW_STATES = NIGERIA_STATES_GRID.map((s) => s.name).sort((a, b) => a.localeCompare(b));

// Accepts either spelling of the FCT (the directory pages use "FCT")
export function canonicalStateName(name) {
  const value = String(name ?? '').trim();
  if (/^(fct|abuja|federal capital territory)$/i.test(value)) return 'Federal Capital Territory';
  return OVERVIEW_STATES.find((s) => s.toLowerCase() === value.toLowerCase()) ?? null;
}

const STATE_ZONES = {
  'North West':    ['Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Sokoto', 'Zamfara'],
  'North East':    ['Adamawa', 'Bauchi', 'Borno', 'Gombe', 'Taraba', 'Yobe'],
  'North Central': ['Benue', 'Federal Capital Territory', 'Kogi', 'Kwara', 'Nasarawa', 'Niger', 'Plateau'],
  'South West':    ['Ekiti', 'Lagos', 'Ogun', 'Ondo', 'Osun', 'Oyo'],
  'South East':    ['Abia', 'Anambra', 'Ebonyi', 'Enugu', 'Imo'],
  'South South':   ['Akwa Ibom', 'Bayelsa', 'Cross River', 'Delta', 'Edo', 'Rivers'],
};

export function zoneOf(state) {
  return Object.keys(STATE_ZONES).find((zone) => STATE_ZONES[zone].includes(state)) ?? 'North Central';
}

// A representative set of each state's LGAs
const STATE_LGAS = {
  Abia: ['Aba North', 'Aba South', 'Umuahia North', 'Umuahia South', 'Ohafia', 'Arochukwu'],
  Adamawa: ['Yola North', 'Yola South', 'Mubi North', 'Mubi South', 'Numan', 'Ganye'],
  'Akwa Ibom': ['Uyo', 'Eket', 'Ikot Ekpene', 'Oron', 'Abak', 'Itu'],
  Anambra: ['Awka South', 'Onitsha North', 'Nnewi North', 'Ihiala', 'Aguata', 'Ekwusigo'],
  Bauchi: ['Bauchi', 'Katagum', 'Misau', 'Ningi', 'Toro', 'Alkaleri'],
  Bayelsa: ['Yenagoa', 'Ogbia', 'Sagbama', 'Brass', 'Nembe', 'Ekeremor'],
  Benue: ['Makurdi', 'Gboko', 'Otukpo', 'Katsina-Ala', 'Guma', 'Vandeikya'],
  Borno: ['Maiduguri', 'Jere', 'Biu', 'Konduga', 'Monguno', 'Dikwa'],
  'Cross River': ['Calabar Municipal', 'Calabar South', 'Ikom', 'Ogoja', 'Obudu', 'Yakurr'],
  Delta: ['Warri South', 'Oshimili South', 'Ughelli North', 'Sapele', 'Ethiope East', 'Ika South'],
  Ebonyi: ['Abakaliki', 'Afikpo North', 'Ezza North', 'Ikwo', 'Ohaukwu', 'Onicha'],
  Edo: ['Oredo', 'Egor', 'Ikpoba-Okha', 'Etsako West', 'Esan West', 'Owan East'],
  Ekiti: ['Ado-Ekiti', 'Ikere', 'Ijero', 'Ikole', 'Oye', 'Emure'],
  Enugu: ['Enugu North', 'Enugu South', 'Nsukka', 'Udi', 'Awgu', 'Oji River'],
  'Federal Capital Territory': ['Abuja Municipal', 'Bwari', 'Gwagwalada', 'Kuje', 'Kwali', 'Abaji'],
  Gombe: ['Gombe', 'Akko', 'Billiri', 'Dukku', 'Kaltungo', 'Yamaltu-Deba'],
  Imo: ['Owerri Municipal', 'Owerri North', 'Okigwe', 'Orlu', 'Aboh Mbaise', 'Oguta'],
  Jigawa: ['Dutse', 'Hadejia', 'Gumel', 'Kazaure', 'Maigatari', 'Ringim'],
  Kaduna: ['Kaduna North', 'Kaduna South', 'Zaria', 'Igabi', 'Chikun', 'Birnin Gwari', 'Lere', "Jema'a"],
  Kano: ['Kano Municipal', 'Fagge', 'Nassarawa', 'Gwarzo', 'Dawakin Tofa', 'Wudil', 'Bichi'],
  Katsina: ['Katsina', 'Daura', 'Funtua', 'Malumfashi', 'Dutsin-Ma', "Mai'Adua"],
  Kebbi: ['Birnin Kebbi', 'Argungu', 'Yauri', 'Zuru', 'Jega', 'Bagudo'],
  Kogi: ['Lokoja', 'Okene', 'Idah', 'Kabba/Bunu', 'Ankpa', 'Dekina'],
  Kwara: ['Ilorin East', 'Ilorin West', 'Ilorin South', 'Offa', 'Patigi', 'Baruten'],
  Lagos: ['Ikeja', 'Agege', 'Alimosho', 'Kosofe', 'Ikorodu', 'Epe', 'Badagry'],
  Nasarawa: ['Lafia', 'Keffi', 'Akwanga', 'Doma', 'Karu', 'Nasarawa'],
  Niger: ['Chanchaga', 'Bida', 'Kontagora', 'Suleja', 'Mokwa', 'Shiroro'],
  Ogun: ['Abeokuta South', 'Abeokuta North', 'Ijebu Ode', 'Sagamu', 'Ado-Odo/Ota', 'Yewa South'],
  Ondo: ['Akure South', 'Akure North', 'Ondo West', 'Owo', 'Okitipupa', 'Akoko North-East'],
  Osun: ['Osogbo', 'Ilesa East', 'Ife Central', 'Ede North', 'Iwo', 'Ifelodun'],
  Oyo: ['Ibadan North', 'Ibadan South-West', 'Egbeda', 'Ogbomosho North', 'Oyo East', 'Iseyin', 'Saki West'],
  Plateau: ['Jos North', 'Jos South', 'Bokkos', 'Mangu', 'Pankshin', 'Shendam'],
  Rivers: ['Port Harcourt', 'Obio/Akpor', 'Eleme', 'Ikwerre', 'Ahoada East', 'Bonny'],
  Sokoto: ['Sokoto North', 'Sokoto South', 'Wamakko', 'Tambuwal', 'Gwadabawa', 'Illela'],
  Taraba: ['Jalingo', 'Wukari', 'Takum', 'Gashaka', 'Bali', 'Zing'],
  Yobe: ['Damaturu', 'Potiskum', 'Bade', 'Nguru', 'Geidam', 'Fika'],
  Zamfara: ['Gusau', 'Kaura Namoda', 'Talata Mafara', 'Anka', 'Tsafe', 'Bungudu'],
};

export function lgasOf(state) {
  return STATE_LGAS[state] ?? [];
}

// Zone-flavoured place and personal names so each state reads like itself
const COMMUNITIES = {
  'North West':    ['Sabon Gari', 'Tudun Wada', 'Unguwan Rimi', 'Kofar Gabas', 'Yan Kasuwa', 'Gidan Dawa', 'Bakin Ruwa', 'Dan Magaji'],
  'North East':    ['Sabon Gari', 'Bulumkutu', 'Gwange', 'Jimeta', 'Kasuwan Shanu', 'Pantami', 'Hayin Gada', 'Gidan Wanzam'],
  'North Central': ['Modern Market', 'Old Garage', 'Wurukum', 'High Level', 'Tudun Wada', 'Angwan Rogo', 'Gado Nasko', 'Ganaja'],
  'South West':    ['Oja Oba', 'Isale', 'Oke-Odo', 'Ita-Oluwo', 'Agbeni', 'Oje', 'Isaba', 'Oke-Ado'],
  'South East':    ['Nkwo', 'Eke', 'Orie', 'Afor', 'Ogbete', 'Umudike', 'Amaokpu', 'Ama-Hausa'],
  'South South':   ['Waterside', 'Old Market', 'Etete', 'New Layout', 'Ugbighoko', 'Watt', 'Marian', 'Iyowa'],
};

const PEOPLE = {
  North: {
    first: ['Musa', 'Aminu', 'Halima', 'Fatima', 'Ibrahim', 'Aisha', 'Usman', 'Zainab', 'Abubakar', 'Hauwa', 'Sani', 'Maryam', 'Yakubu', 'Bilkisu', 'Garba', 'Hadiza'],
    last:  ['Bello', 'Abdullahi', 'Sule', 'Danjuma', 'Lawal', 'Yusuf', 'Garba', 'Aliyu', 'Umar', 'Tanko', 'Shehu', 'Ibrahim', 'Kabir', 'Mohammed'],
  },
  'North Central': {
    first: ['Samson', 'Grace', 'Daniel', 'Ruth', 'Emmanuel', 'Esther', 'John', 'Comfort', 'Peter', 'Mary', 'Ishaya', 'Rifkatu', 'Joseph', 'Hannah'],
    last:  ['Ochai', 'Iorliam', 'Tersoo', 'Danladi', 'Gyang', 'Pam', 'Audu', 'Agbo', 'Okpe', 'Akaa', 'Dakwo', 'Alhassan'],
  },
  'South West': {
    first: ['Adebayo', 'Funmilayo', 'Olumide', 'Bisi', 'Tunde', 'Kemi', 'Segun', 'Yetunde', 'Femi', 'Bola', 'Kunle', 'Toyin', 'Wale', 'Ronke'],
    last:  ['Adeyemi', 'Ogunleye', 'Bakare', 'Adeleke', 'Ojo', 'Akinola', 'Oyelaran', 'Balogun', 'Adewale', 'Olaniyan', 'Fashola', 'Adebisi'],
  },
  'South East': {
    first: ['Chinedu', 'Ngozi', 'Emeka', 'Adaeze', 'Obinna', 'Chioma', 'Ifeanyi', 'Nkechi', 'Uche', 'Amaka', 'Kelechi', 'Ebere', 'Chukwudi', 'Ifeoma'],
    last:  ['Okafor', 'Nwosu', 'Eze', 'Okeke', 'Obi', 'Nwachukwu', 'Okonkwo', 'Anyanwu', 'Ugwu', 'Onyekachi', 'Nnadi', 'Ezeh'],
  },
  'South South': {
    first: ['Efe', 'Ebiere', 'Ita', 'Ime', 'Osaro', 'Tamuno', 'Ofonime', 'Ebi', 'Eseoghene', 'Uduak', 'Osagie', 'Preye', 'Aniekan', 'Ivie'],
    last:  ['Akpan', 'Etim', 'Bassey', 'Okon', 'Ighodaro', 'Omoregie', 'Tamuno', 'Ebiye', 'Edet', 'Ogbeide', 'Udo', 'Briggs'],
  },
};

function peopleOf(zone) {
  if (zone === 'North West' || zone === 'North East') return PEOPLE.North;
  return PEOPLE[zone];
}

// ── Deterministic randomness ─────────────────────────────────────────────────
function hashString(text) {
  let h = 2166136261;
  for (const ch of String(text)) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seededRandom(seed) {
  let a = hashString(seed);
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pick = (rand, list) => list[Math.floor(rand() * list.length)];

function shuffled(rand, list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function phone(rand) {
  const prefix = pick(rand, ['703', '706', '803', '806', '813', '815', '802', '805', '809', '701']);
  const part = () => String(Math.floor(rand() * 1000)).padStart(3, '0');
  return `+234 ${prefix} ${part()} ${part()}${Math.floor(rand() * 10)}`;
}

function stateCode(state) {
  if (state === 'Federal Capital Territory') return 'FC';
  return state.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase();
}

// Every column below is a field the internal capture forms collect, using
// those forms' own option lists — nothing is shown that no form feeds.

// Weighted pick: [[value, weight], ...]
function pickWeighted(rand, entries) {
  const total = entries.reduce((a, [, w]) => a + w, 0);
  let r = rand() * total;
  for (const [value, weight] of entries) {
    r -= weight;
    if (r <= 0) return value;
  }
  return entries[entries.length - 1][0];
}

// ── Livestock markets — Market Registry (DCIC-02) ────────────────────────────
// market_name, market_type, market_schedule, status, contact, location,
// established, lga (marketsApi / marketTradOptions)
const MARKET_KINDS = [
  { suffix: 'Cattle Market',     type: 'Live Animal',       weight: 5 },
  { suffix: 'Livestock Market',  type: 'Live Animal',       weight: 5 },
  { suffix: 'Abattoir Market',   type: 'Abattoir Market',   weight: 2 },
  { suffix: 'Meat Market',       type: 'Processing Market', weight: 2 },
  { suffix: 'Feed Market',       type: 'Input / Feed',      weight: 1 },
  { suffix: 'Central Market',    type: 'Mixed',             weight: 2 },
  { suffix: 'Terminal Market',   type: 'Terminal Market',   weight: 1 },
];
const MARKET_SCHEDULES = [['Daily', 3], ['Weekly', 6], ['Bi-Weekly', 2], ['Monthly', 1], ['Periodic', 1], ['As Needed', 0.5]];
const MARKET_STATUSES = [['Active', 85], ['Seasonal', 7], ['Under Rehabilitation', 5], ['Inactive', 3]];
const LOCATION_HINTS = ['Along', 'Off', 'Beside', 'Opposite'];
const LOCATION_ROADS = ['Main Road', 'Old Motor Park', 'Central Mosque Road', 'Market Road', 'Express Way', 'Railway Crossing', 'Government Secondary School', 'LGA Secretariat'];

function buildMarkets(state, count) {
  const rand = seededRandom(`markets:${state}`);
  const lgas = lgasOf(state);
  const communities = COMMUNITIES[zoneOf(state)];
  const kinds = MARKET_KINDS.flatMap((kind) => Array(kind.weight).fill(kind));
  const combos = shuffled(rand, lgas.flatMap((lga) => communities.flatMap((community) => kinds.map((kind) => ({ lga, community, kind })))));
  const used = new Set();

  return Array.from({ length: count }, (_, i) => {
    const { lga, community, kind } = combos[i % combos.length];
    // Same community names recur across LGAs — qualify with the LGA when needed
    let name = `${community} ${kind.suffix}`;
    if (used.has(name)) name = `${community} ${kind.suffix}, ${lga}`;
    let n = 2;
    while (used.has(name)) name = `${community} ${kind.suffix}, ${lga} ${n++}`;
    used.add(name);
    return {
      id: `mkt-${stateCode(state)}-${i + 1}`,
      name,
      lga,
      location: `${pick(rand, LOCATION_HINTS)} ${pick(rand, LOCATION_ROADS)}, ${community}`,
      type: kind.type,
      schedule: pickWeighted(rand, MARKET_SCHEDULES),
      established: String(1960 + Math.floor(rand() * 64)),
      status: pickWeighted(rand, MARKET_STATUSES),
      contact: phone(rand),
    };
  });
}

// ── Registered livestock facilities — Facility Core Registry (DCIC-06) ──────
// name, facility_type, ownership, functional_status, licensed_status, lga,
// and the type-specific capacity_metadata fields (facilitiesApi). Facilities
// carry no contact or registration number, so none is shown.
const FACILITY_KINDS = [
  { type: 'Veterinary Clinic',         suffix: 'Veterinary Clinic',          weight: 4 },
  { type: 'Veterinary Hospital',       suffix: 'Veterinary Hospital',        weight: 1 },
  { type: 'Laboratory',                suffix: 'Veterinary Laboratory',      weight: 1 },
  { type: 'Diagnostic Centre',         suffix: 'Animal Diagnostic Centre',   weight: 1 },
  { type: 'Abattoir',                  suffix: 'Abattoir',                   weight: 3, capacity: { label: 'Daily Slaughter', unit: '/day', range: [40, 600] } },
  { type: 'Slaughter Slab',            suffix: 'Slaughter Slab',             weight: 3, capacity: { label: 'Daily Slaughter', unit: '/day', range: [10, 80] } },
  { type: 'Grazing Reserve',           suffix: 'Grazing Reserve',            weight: 1 },
  { type: 'Watering Point',            suffix: 'Watering Point',             weight: 2 },
  { type: 'Milk Collection Centre',    suffix: 'Milk Collection Centre',     weight: 2, capacity: { label: 'Daily Milk', unit: ' L/day', range: [1000, 20000] } },
  { type: 'Feed Mill',                 suffix: 'Feed Mill',                  weight: 2, capacity: { label: 'Annual Production', unit: ' Tonnes', range: [500, 20000] } },
  { type: 'Cold Storage (Commercial)', suffix: 'Cold Store',                 weight: 1, capacity: { label: 'Cold Storage', unit: ' Tonnes', range: [20, 400] } },
  { type: 'Breeding Farm',             suffix: 'Breeding Farm',              weight: 1 },
  { type: 'AI Centre',                 suffix: 'AI Centre',                  weight: 1 },
  { type: 'Quarantine Station',        suffix: 'Quarantine Station',         weight: 1 },
  { type: 'Livestock Farm',            suffix: 'Livestock Farm',             weight: 2 },
];
const OWNERSHIP = [['Federal', 2], ['State', 4], ['LGA', 3], ['Private', 5], ['Community', 2], ['NGO Partner', 0.5], ['PPP', 0.5], ['Religious', 0.3]];
const FUNCTIONAL_STATUSES = [['Active', 75], ['Under Maintenance', 9], ['Under Rehabilitation', 8], ['Inactive', 6], ['Decommissioned', 2]];
const LICENSED_STATUSES = [['Licensed', 70], ['Pending', 15], ['Unlicensed', 10], ['Exempt', 5]];

function roundCapacity(value) {
  if (value >= 10000) return Math.round(value / 1000) * 1000;
  if (value >= 1000) return Math.round(value / 100) * 100;
  if (value >= 100) return Math.round(value / 10) * 10;
  return value;
}

function buildFacilities(state, count) {
  const rand = seededRandom(`facilities:${state}`);
  const lgas = lgasOf(state);
  const kinds = FACILITY_KINDS.flatMap((kind) => Array(kind.weight).fill(kind));
  const combos = shuffled(rand, lgas.flatMap((lga) => kinds.map((kind) => ({ lga, kind }))));
  const used = new Set();

  return Array.from({ length: count }, (_, i) => {
    const { lga, kind } = combos[i % combos.length];
    let name = `${lga} ${kind.suffix}`;
    let n = 2;
    while (used.has(name)) name = `${lga} ${kind.suffix} ${n++}`;
    used.add(name);
    let capacity = '—';
    if (kind.capacity) {
      const [min, max] = kind.capacity.range;
      capacity = `${kind.capacity.label}: ${roundCapacity(min + Math.floor(rand() * (max - min))).toLocaleString('en-NG')}${kind.capacity.unit}`;
    }
    return {
      id: `fac-${stateCode(state)}-${i + 1}`,
      name,
      lga,
      type: kind.type,
      ownership: pickWeighted(rand, OWNERSHIP),
      capacity,
      status: pickWeighted(rand, FUNCTIONAL_STATUSES),
      licensed: pickWeighted(rand, LICENSED_STATUSES),
    };
  });
}

// ── Registered veterinarians — Livestock Actor Registry, actor type
// Veterinarian (workforceApi): full_name, contact_phone, lga,
// ward_community, registration_number, species_specialisation, status,
// verification_status
const SPECIES_SPECIALISATIONS = [['Cattle', 5], ['Small Ruminants', 4], ['Poultry', 5], ['Pig', 1], ['Mixed Species', 4], ['Camel', 0.5], ['Companion Animals', 1]];
const ACTOR_STATUSES = [['Active', 88], ['On Leave', 5], ['Inactive', 5], ['Suspended', 2]];
const VERIFICATION = [['Verified', 85], ['Pending', 15]];

function buildVeterinarians(state, count) {
  const rand = seededRandom(`vets:${state}`);
  const lgas = lgasOf(state);
  const zone = zoneOf(state);
  const communities = COMMUNITIES[zone];
  const { first, last } = peopleOf(zone);
  const names = shuffled(rand, first.flatMap((f) => last.map((l) => `${f} ${l}`)));
  const initials = 'ABDEGHIKMNORSTUY';

  return Array.from({ length: count }, (_, i) => {
    const base = names[i % names.length];
    const round = Math.floor(i / names.length);
    const name = round ? base.replace(' ', ` ${initials[(round - 1) % initials.length]}. `) : base;
    const year = 2005 + Math.floor(rand() * 20);
    return {
      id: `vet-${stateCode(state)}-${i + 1}`,
      name: `Dr. ${name}`,
      lga: pick(rand, lgas),
      ward: pick(rand, communities),
      registration: `VCN/${year}/${String(Math.floor(rand() * 9000) + 100).padStart(5, '0')}`,
      specialisation: pickWeighted(rand, SPECIES_SPECIALISATIONS),
      status: pickWeighted(rand, ACTOR_STATUSES),
      verification: pickWeighted(rand, VERIFICATION),
      contact: phone(rand),
    };
  });
}

// ── Livestock population — Population Record (DCIC-01) ───────────────────────
// lga, species, breed/ecotype, production_system, production_purpose,
// population_count, herds_flocks_count, reference_period, count_method and
// the confidence_level derived from it. One row per LGA × species; rows sum
// exactly to the state's published population.
const NORTHERN = { cattle: ['White Fulani (Bunaji)', 'Red Bororo', 'Sokoto Gudali'], sheep: ['Yankasa', 'Uda', 'Balami'], goat: ['Red Sokoto', 'Sahel'] };
const SOUTHERN = { cattle: ['Muturu', "N'Dama", 'Keteku'], sheep: ['West African Dwarf', 'Yankasa'], goat: ['West African Dwarf', 'Red Sokoto'] };

// Species codes and production purposes as the population form defines them
const PURPOSE_LABELS = {
  beef: 'Beef', dairy: 'Dairy', breeding: 'Breeding', draught: 'Draught', dual: 'Dual Purpose',
  meat: 'Meat', wool: 'Wool', eggs: 'Eggs', festival_fattening: 'Festival Fattening',
};
const PRODUCTION_SYSTEMS = ['Pastoral', 'Agropastoral', 'Commercial', 'Backyard', 'Nomadic', 'Extensive'];
// count_method → confidence_level, as LivestockAddRecord derives it
const COUNT_METHODS = [['Physical Count', 'High'], ['NAITS Registry', 'Medium'], ['LGA Records', 'Medium'], ['Estimate', 'Low'], ['Aerial Survey', 'Medium']];

function speciesProfiles(zone) {
  const isNorth = zone === 'North West' || zone === 'North East';
  const breeds = isNorth || zone === 'North Central' ? NORTHERN : SOUTHERN;
  const flock = [40, 400];
  const herd = [8, 60];
  const profiles = [
    { species: 'Cattle', weight: isNorth ? 30 : zone === 'North Central' ? 20 : 6, breeds: breeds.cattle, purposes: ['beef', 'dairy', 'breeding', 'draught', 'dual'], systems: isNorth ? ['Pastoral', 'Agropastoral', 'Nomadic'] : ['Agropastoral', 'Commercial', 'Extensive'], herd: [20, 90], methods: isNorth ? [0, 1, 2, 4] : [0, 1, 2] },
    { species: 'Sheep', weight: isNorth ? 20 : zone === 'North Central' ? 15 : 9, breeds: breeds.sheep, purposes: ['meat', 'wool', 'breeding', 'dual'], systems: ['Extensive', 'Agropastoral', 'Backyard'], herd, methods: [0, 1, 2, 3] },
    { species: 'Goat', weight: 25, breeds: breeds.goat, purposes: ['meat', 'dairy', 'breeding', 'dual'], systems: ['Extensive', 'Backyard', 'Agropastoral'], herd, methods: [0, 1, 2, 3] },
    { species: 'Indigenous Chicken', weight: isNorth ? 12 : 18, breeds: ['Nigerian Indigenous Chicken'], purposes: ['eggs', 'meat', 'dual'], systems: ['Backyard', 'Extensive'], herd: [15, 80], methods: [2, 3] },
    { species: 'Broiler', weight: isNorth ? 5 : 14, breeds: ['Arbor Acres', 'Cobb 500', 'Ross 308'], purposes: ['meat'], systems: ['Commercial'], herd: [500, 5000], methods: [0, 2] },
    { species: 'Layer', weight: isNorth ? 4 : 12, breeds: ['Isa Brown', 'Lohmann Brown', 'Bovans Brown'], purposes: ['eggs', 'dual'], systems: ['Commercial'], herd: [500, 8000], methods: [0, 2] },
    { species: 'Guinea Fowl', weight: isNorth ? 2 : 1, breeds: ['Pearl Guinea Fowl'], purposes: ['meat', 'eggs', 'dual'], systems: ['Backyard', 'Extensive'], herd: flock, methods: [2, 3] },
    { species: 'Pig', weight: isNorth ? 0.6 : zone === 'North Central' ? 6 : 10, breeds: ['Large White', 'Landrace', 'Indigenous'], purposes: ['meat', 'festival_fattening'], systems: ['Commercial', 'Backyard'], herd: [10, 120], methods: [0, 2, 3] },
  ];
  if (isNorth) {
    profiles.push({ species: 'Camel', weight: 1.5, breeds: ['Dromedary (Yoruk)'], purposes: ['dairy', 'draught', 'meat'], systems: ['Pastoral', 'Nomadic'], herd: [5, 40], methods: [1, 3, 4] });
    profiles.push({ species: 'Donkey', weight: 1.5, breeds: ['Nigerian Donkey'], purposes: ['draught', 'breeding'], systems: ['Agropastoral', 'Backyard'], herd: [2, 15], methods: [2, 3] });
  } else {
    profiles.push({ species: 'Turkey', weight: 1.5, breeds: ['Local Turkey', 'Broad Breasted Bronze'], purposes: ['meat', 'eggs'], systems: ['Backyard', 'Commercial'], herd: [10, 200], methods: [0, 2, 3] });
    profiles.push({ species: 'Rabbit', weight: 1, breeds: ['New Zealand White', 'Chinchilla'], purposes: ['meat'], systems: ['Backyard', 'Commercial'], herd: [10, 150], methods: [0, 2] });
  }
  return profiles;
}

const PERIODS = ['2025-Q2', '2025-Q1', '2024-Q4'];

function buildPopulation(state, total) {
  const rand = seededRandom(`population:${state}`);
  const zone = zoneOf(state);
  const lgas = lgasOf(state);
  const lgaWeights = lgas.map(() => 0.6 + rand());

  const rows = [];
  lgas.forEach((lga, li) => {
    speciesProfiles(zone).forEach((profile) => {
      const [method, confidence] = COUNT_METHODS[pick(rand, profile.methods)];
      rows.push({
        id: `pop-${stateCode(state)}-${li + 1}-${profile.species.replace(/\s+/g, '-').toLowerCase()}`,
        lga,
        species: profile.species,
        breed: pick(rand, profile.breeds),
        system: pick(rand, profile.systems.filter((s) => PRODUCTION_SYSTEMS.includes(s))),
        purpose: PURPOSE_LABELS[pick(rand, profile.purposes)],
        weight: lgaWeights[li] * profile.weight * (0.75 + rand() * 0.5),
        herdSize: profile.herd,
        period: pickWeighted(rand, [[PERIODS[0], 6], [PERIODS[1], 3], [PERIODS[2], 1]]),
        method,
        confidence,
      });
    });
  });

  // Scale the weights to the published total, then put any rounding
  // difference on the largest row so the sum is exact
  const weightSum = rows.reduce((a, r) => a + r.weight, 0);
  rows.forEach((r) => { r.population = Math.round((r.weight / weightSum) * total); });
  const diff = total - rows.reduce((a, r) => a + r.population, 0);
  rows.reduce((max, r) => (r.population > max.population ? r : max), rows[0]).population += diff;

  return rows.map(({ weight, herdSize, ...row }) => {
    const [min, max] = herdSize;
    const avg = min + rand() * (max - min);
    return { ...row, herds: Math.max(1, Math.round(row.population / avg)) };
  });
}

// Per-species totals for the population tab's summary strip
export function summarizeSpecies(rows) {
  const totals = new Map();
  rows.forEach((r) => totals.set(r.species, (totals.get(r.species) || 0) + r.population));
  const total = rows.reduce((a, r) => a + r.population, 0);
  return [...totals.entries()]
    .map(([species, population]) => ({ species, population, share: total ? (population / total) * 100 : 0 }))
    .sort((a, b) => b.population - a.population);
}

// ── Public API ───────────────────────────────────────────────────────────────
const cache = new Map();

// Everything the State Overview page shows for one state
export function getStateOverview(state) {
  if (cache.has(state)) return cache.get(state);
  const details = getStateDetails(state);
  const overview = {
    details,
    zone: zoneOf(state),
    lgas: lgasOf(state),
    population: buildPopulation(state, details.livestockPopulation),
    markets: buildMarkets(state, details.livestockMarkets),
    facilities: buildFacilities(state, details.registeredFacilities),
    veterinarians: buildVeterinarians(state, details.registeredVeterinarians),
  };
  cache.set(state, overview);
  return overview;
}

export const STATE_OVERVIEW_LAST_UPDATED = '13 Aug. 2025';
